// 模型配置与 aiMuse 剪辑上下文统一存 IndexedDB（settings store）。
// 前端自闭环：模型密钥只在本机 IndexedDB；aiMuse 登录票据仅用于 /api/llm 与 /nle 数据接口。
import * as db from './db.js';

const KEY = 'llm';

/**
 * 归一化「本集分镜素材同步基线」。
 * 结构：{ [项目ID:章节ID]: { [storyboardId]: { videoUrl, materialId, path, name, duration, updatedAt, syncedAt } } }。
 * 只保留能定位视频的记录，避免损坏的结构反复写回 IndexedDB。
 */
export function normalizeStoryboardMaterialVersions(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const result = {};
    for (const [chapterKey, entries] of Object.entries(raw)) {
        if (!chapterKey || !entries || typeof entries !== 'object' || Array.isArray(entries)) continue;
        const normalizedEntries = {};
        for (const [storyboardId, item] of Object.entries(entries)) {
            if (!storyboardId || !item || typeof item !== 'object' || Array.isArray(item)) continue;
            const videoUrl = String(item.videoUrl || '').trim();
            if (!videoUrl) continue;
            normalizedEntries[storyboardId] = {
                videoUrl,
                materialId: String(item.materialId || ''),
                path: String(item.path || ''),
                name: String(item.name || ''),
                duration: Number(item.duration) || 0,
                updatedAt: String(item.updatedAt || ''),
                syncedAt: String(item.syncedAt || ''),
            };
        }
        if (Object.keys(normalizedEntries).length) {
            result[chapterKey] = normalizedEntries;
        }
    }
    return result;
}

export async function getLlmSettings() {
    const record = await db.get('settings', KEY);
    return {
        apiKey: '',
        baseURL: '',
        modelName: '',
        accessToken: '',
        // aiMuse 数据接口上下文：由剧集入口页写入，编辑器用于素材导入与草稿同步。
        apiBase: '',
        appVersion: '',
        museProjectId: '',
        museChapterId: '',
        variant: 'original',
        museDraftVersion: 0,
        importedStoryboardIds: [],
        // 分镜视频同步基线：记录上次导入 / 同步时选中的视频地址，用于判断是否重抽或换历史视频。
        storyboardMaterialVersions: {},
        ...(record || {}),
    };
}

export async function saveLlmSettings(settings) {
    const importedStoryboardIds = Array.isArray(settings?.importedStoryboardIds)
        ? settings.importedStoryboardIds.map(item => String(item)).filter(Boolean)
        : [];
    const storyboardMaterialVersions = normalizeStoryboardMaterialVersions(settings?.storyboardMaterialVersions);
    const normalized = {
        key: KEY,
        apiKey: String(settings?.apiKey ?? ''),
        baseURL: String(settings?.baseURL ?? ''),
        modelName: String(settings?.modelName ?? ''),
        // aiMuse 登录 token：仅用于剪辑器自己的 /api/llm 代理做身份校验，不会下发给模型供应商。
        accessToken: String(settings?.accessToken ?? ''),
        apiBase: String(settings?.apiBase ?? '').replace(/\/+$/, ''),
        appVersion: String(settings?.appVersion ?? ''),
        museProjectId: String(settings?.museProjectId ?? ''),
        museChapterId: String(settings?.museChapterId ?? ''),
        variant: String(settings?.variant ?? 'original') === 'enhance' ? 'enhance' : 'original',
        museDraftVersion: Number(settings?.museDraftVersion) || 0,
        importedStoryboardIds,
        storyboardMaterialVersions,
    };
    await db.put('settings', normalized);
    return normalized;
}

export default { getLlmSettings, saveLlmSettings };
